import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DeviceProvider } from "@/context/DeviceContext";
import { ProspectsProvider } from "@/context/ProspectsContext";
import { TrackingProvider } from "@/context/TrackingContext";
import { DevicePreviewToggle } from "@/components/DevicePreviewToggle";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { OfflineSyncManager } from "@/components/OfflineSyncManager";
import { Toaster } from "sonner";

import Splash from "@/pages/auth/Splash";
import Login from "@/pages/auth/Login";

import { SalesLayout } from "@/components/sales/SalesLayout";
import Dashboard from "@/pages/sales/Dashboard";
import LiveTracking from "@/pages/sales/LiveTracking";
import Prospects from "@/pages/sales/Prospects";
import ProspectDetail from "@/pages/sales/ProspectDetail";
import AddProspect from "@/pages/sales/AddProspect";
import Performance from "@/pages/sales/Performance";
import Profile from "@/pages/sales/Profile";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import { ROLES } from "@/constants/config";

const ADMIN_ROUTES = [
  "/admin/dashboard",
  "/admin/live-map",
  "/admin/salesman",
  "/admin/prospek",
  "/admin/bukti-foto",
  "/admin/analytics",
  "/admin/coverage-map",
  "/admin/dealer",
  "/admin/users",
  "/admin/audit-log",
  "/admin/settings",
];

const SmartRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Splash />;
  if (user.role === ROLES.SALES) return <Navigate to="/sales/home" replace />;
  return <Navigate to="/admin/dashboard" replace />;
};

const ScrollReset = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const SalesProtected = ({ children }) => (
  <ProtectedRoute allowedRoles={[ROLES.SALES]}>{children}</ProtectedRoute>
);

const AdminProtected = ({ children }) => (
  <ProtectedRoute allowedRoles={[ROLES.MAIN_DEALER, ROLES.KEPALA_CABANG, ROLES.TEAM_LEADER]}>
    {children}
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <DeviceProvider>
        <AuthProvider>
          <TrackingProvider>
            <ProspectsProvider>
              <BrowserRouter>
                <ScrollReset />
                <DevicePreviewToggle />
                <OfflineSyncManager />
                <Toaster position="top-center" richColors />
                <Routes>
                  <Route path="/" element={<SmartRedirect />} />
                  <Route path="/login" element={<Login />} />

                  {/* Salesman routes nested under SalesLayout */}
                  <Route
                    element={
                      <SalesProtected>
                        <SalesLayout />
                      </SalesProtected>
                    }
                  >
                    <Route path="/sales/home" element={<Dashboard />} />
                    <Route path="/sales/dashboard" element={<Navigate to="/sales/home" replace />} />
                    <Route path="/sales/tracking" element={<LiveTracking />} />
                    <Route path="/sales/coverage" element={<LiveTracking />} />
                    <Route path="/sales/prospek" element={<Prospects />} />
                    <Route path="/sales/prospek/:id" element={<ProspectDetail />} />
                    <Route path="/sales/prospects" element={<Prospects />} />
                    <Route path="/sales/prospects/:id" element={<ProspectDetail />} />
                    <Route path="/sales/add" element={<AddProspect />} />
                    <Route path="/sales/performance" element={<Performance />} />
                    <Route path="/sales/profile" element={<Profile />} />
                  </Route>

                  {/* Admin routes */}
                  {ADMIN_ROUTES.map((path) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <AdminProtected>
                          <AdminDashboard />
                        </AdminProtected>
                      }
                    />
                  ))}

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
            </ProspectsProvider>
          </TrackingProvider>
        </AuthProvider>
      </DeviceProvider>
    </div>
  );
}

export default App;
